import { NOTIFICATIONS_BANNER_IFRAME_ID, ONBOARDING_TOURS_BANNER_ID, HS_NAV_BEFORE_CONTAINER_ID, GROWTH_DYNAMIC_UI_ID } from './NavLayoutDimensions/constants';
import { setNavPositions } from './NavLayoutDimensions/utils/setNavPositions';
import { getExternalBannersHeight } from './NavLayoutDimensions/utils/getExternalBannersHeight';
import { updateExternalnavComponentDimension } from './NavLayoutDimensions/utils/updateExternalnavComponentDimension';
import { setNavStartTime } from './utils/navStartTime';
import setSpeculationRulesScript from './utils/SpeculationRules/setSpeculationRulesScript';
import { MagpieConnection } from 'magpie-lib/connection';
import { Tags } from 'magpie-types/tags';
import { getPortalId } from './utils/getPortalId';
import { Level, Logger } from 'simple-logging-lib';
import { UNIFIED_NAVIGATION_UI_LOGGER_NAME } from './constants/logging';
import isLocal from './utils/isLocal';
import { ErrorLogHandler } from './errors/ErrorLogHandler';
import { defaultErrorLogFilter } from './errors/filters/defaultErrorLogFilter';
let setNavHeightTimer;
const setNavHeightAndWidth = store => {
  updateExternalnavComponentDimension('Banners', {
    height: getExternalBannersHeight()
  }, store.dispatch);
};
const shouldObserveElement = el => {
  const containerIdsAboveNav = [NOTIFICATIONS_BANNER_IFRAME_ID, ONBOARDING_TOURS_BANNER_ID, HS_NAV_BEFORE_CONTAINER_ID, GROWTH_DYNAMIC_UI_ID];
  if (el && el.id && containerIdsAboveNav.includes(el.id)) {
    return true;
  }
  if (!el || !el.classList) {
    return false;
  }
  const classesToObserve = ['fire-alarm_alarms'];
  const includesClasses = classesToObserve.filter(c => el.classList.contains(c));
  return includesClasses.length > 0;
};
export default class Application {
  constructor() {
    this.logger = Logger.getLogger(UNIFIED_NAVIGATION_UI_LOGGER_NAME);
    this.setupLogging();
  }
  setupLogging() {
    this.logger.setLevel(isLocal() ? Level.DEBUG : Level.ERROR);
    const handler = new ErrorLogHandler();
    handler.setFilter(defaultErrorLogFilter);
    this.logger.addHandler(handler);
  }

  // Kicks off the app. This is intentionally not in the constructor just in
  // Case there is a reason you need to explicitly call init (and don't want it
  // Auto-called by importing this module)

  callback(store) {
    setSpeculationRulesScript(store);
    const navTopLevel = document.getElementById('hs-nav-v4');

    // Resize observer to set the nav height css variable when anything is added above the
    // navigation div. For e.g., banner notification iframe
    const navHeightResizeObserver = new ResizeObserver(entries => {
      entries.forEach(() => {
        setNavPositions();
        setNavHeightAndWidth(store);
      });
    });
    if (navTopLevel) {
      navHeightResizeObserver.observe(navTopLevel);
    }
    const config = {
      childList: true
    };
    const bodyElMutationObserver = new MutationObserver(mutationList => {
      for (const mutation of mutationList) {
        // Only concerned with direct descendents of the page body
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            // Ignore anything other than elements that are added
            if (node.ELEMENT_NODE === 1) {
              const el = node;
              if (shouldObserveElement(el)) {
                // This array should contain the list of all element ids that
                // could potentially be added above the nav and outside the nav
                // container(#hs - nav - v4) e.g.banner notifications

                // Only update the height once if multiple items are added in
                // quick succession
                if (setNavHeightTimer) {
                  clearTimeout(setNavHeightTimer);
                }

                // Delay setting the nav height as external containers like growth-ui banners
                // aren't fully rendered immediately
                setNavHeightTimer = setTimeout(() => {
                  setNavPositions();
                  setNavHeightAndWidth(store);
                }, 750);

                // Notifications banners are added with a transition so this will update
                // the height quicker to give a smoother experience
                node.addEventListener('transitionend', () => {
                  setNavPositions();
                  setNavHeightAndWidth(store);
                }, {
                  once: true
                });

                // As these items are added outside the nav, we need to watch them individually for size changes
                const containerOutsideNav = document.getElementById(el.id);
                if (containerOutsideNav) {
                  navHeightResizeObserver.observe(containerOutsideNav);
                }
              }
            }
          }

          // Unobserve items if they are removed
          for (const node of mutation.removedNodes) {
            // Ignore anything other then elements that are removed
            if (node.ELEMENT_NODE === 1) {
              const el = node;
              if (shouldObserveElement(el)) {
                // Set the new nav height before unobserving the element
                setTimeout(() => {
                  setNavPositions();
                  setNavHeightAndWidth(store);
                }, 0);
                navHeightResizeObserver.unobserve(el);
              }
            }
          }
        }
      }
    });
    bodyElMutationObserver.observe(document.body, config);
  }
  start(render) {
    setNavStartTime(performance.now());
    this.setupCustomEventPolyfill();
    // Setting up the Magpie connection before the Nav components are mounted below.
    // This ensures the connection is ready for any nested components that may rely on
    // it after they are mounted.
    this.createMagpieConnection();
    render(this.callback.bind(this));
  }
  setupCustomEventPolyfill() {
    if (typeof window.CustomEvent !== 'function') {
      const CustomEvent = function CustomEvent(event, params) {
        params = params || {
          bubbles: false,
          cancelable: false,
          detail: undefined
        };
        const evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
      };
      CustomEvent.prototype = window.Event.prototype;
      window.CustomEvent = CustomEvent;
    }
  }
  createMagpieConnection() {
    const isDebugEnabled = () => {
      try {
        return localStorage.getItem('NAVIGATION_MAGPIE_DEBUG') === 'true';
      } catch (e) {
        return false;
      }
    };
    MagpieConnection.create({
      appName: 'unified-navigation-ui',
      tags: [Tags.NAV],
      portalId: getPortalId(),
      debug: isDebugEnabled(),
      onConnectionError: error => {
        this.logger.error(new Error('Failed to connect using Magpie', {
          cause: error
        }));
      }
    });
  }
}